import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { ErrorMessage } from 'secondstep-components'
import {
  operations as licenseOperations,
  selectors as licenseSelectors,
} from 'store/licenseManager'
import {
  buildReportPath,
  getReportVariantFromPathname,
  parseLessonCompletionReportPathname,
} from 'store/licenseManager/reportRouteHelpers'
import { operations as loadingOperations } from 'store/loadingManager'
import {
  operations as reportsOperations,
  selectors as reportsSelectors,
} from 'store/reportsManager'
import { K8_GRADE_KEY_TO_GRAPH_ENUM } from 'store/reportsManager/constants'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import { navigationEvent } from 'layers/navigation/store/operations'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { REPLACE } from 'layers/navigation/store/types'
import LessonCompletionReport from './component'
import {
  DEFAULT_K8_REPORT_UNIT_SECTION,
  ENTRY_ID,
  getK8ReportFlagOnRedirectTarget,
  K8_GRADE_NAME_TO_GRADE_SLUG_MAP,
  K8_REPORT_UNIT_SECTION,
  REPORT_VARIANT,
} from './constants'
import {
  ACTIVITY_NAME_TO_SLUG_MAP,
  GRADE_NAME_TO_GRADE_SLUG_MAP,
  UNIT_RANGE_NAME_TO_SLUG_MAP,
} from './nameToSlugMap'

export const LessonCompletionReportContainer = ({
  activeReportSites,
  classDetailsShouldFetch,
  classInstance,
  entry,
  errorStatusCode,
  fetchK8ReportClassDetails,
  fetchK8ReportGrade,
  fetchK8ReportGradeAndClassDetails,
  fetchK8ReportSchool,
  fetchUserSites,
  gradeAndClassShouldFetch,
  gradeEmptyDisplay,
  gradeGraphEnum,
  gradeShouldFetch,
  isDwbReportRoute,
  isFetchingUserSites,
  isLoadingApp,
  location,
  match,
  navigationEvent,
  query,
  reportVariant,
  schoolEmptyDisplay,
  schoolShouldFetch,
  selectedSite,
  setAppIsLoadingState,
  setAppNotLoadingState,
  shouldLoad,
  k8ReportUnitSection,
  pathnameNeedsRedirect,
  redirectTargetPath,
  updateSelectedProgram,
  updateSelectedSite,
}) => {
  useEffect(() => {
    updateSelectedProgram(
      isDwbReportRoute
        ? { name: 'DWB', key: 'DWB' }
        : { name: 'K8', key: 'K8' },
    )
  }, [])

  useEffect(() => {
    if (shouldLoad && isLoadingApp) setAppIsLoadingState('Loading content...')
    if (!shouldLoad && !isLoadingApp) setAppNotLoadingState()
  }, [isLoadingApp, shouldLoad])

  useEffect(() => {
    if (!pathnameNeedsRedirect || !redirectTargetPath) {
      return
    }
    const queryParamsByKey = {
      ...(query?.site && { site: query.site }),
      ...(query?.class && { class: query.class }),
    }
    const url = NavigationHelper.generateRouteWithQueryParams(
      redirectTargetPath,
      queryParamsByKey,
    )
    navigationEvent(url, REPLACE)
  }, [
    pathnameNeedsRedirect,
    redirectTargetPath,
    navigationEvent,
    query?.site,
    query?.class,
  ])

  useEffect(() => {
    if (!activeReportSites) {
      fetchUserSites()
    }
    if (schoolShouldFetch) {
      fetchK8ReportSchool(selectedSite)
    }
    if (gradeAndClassShouldFetch) {
      fetchK8ReportGradeAndClassDetails(
        selectedSite,
        gradeGraphEnum,
        classInstance,
      )
    } else {
      if (gradeShouldFetch) {
        fetchK8ReportGrade(selectedSite, gradeGraphEnum)
      }
      if (classDetailsShouldFetch) {
        fetchK8ReportClassDetails(selectedSite, classInstance)
      }
    }
  }, [
    classDetailsShouldFetch,
    classInstance,
    errorStatusCode,
    gradeAndClassShouldFetch,
    gradeGraphEnum,
    gradeShouldFetch,
    schoolShouldFetch,
    selectedSite,
  ])

  useEffect(() => {
    const isReportRoute =
      location.pathname.includes('/reports/k8') ||
      location.pathname.includes('/reports/dwb')

    if (activeReportSites?.length) {
      if (!query.site && isReportRoute) {
        navigationHandler(
          {
            siteId: activeReportSites[0].id,
            ...(query?.class && { class: query.class }),
          },
          REPLACE,
        )
      } else if (!selectedSite || query.site !== selectedSite.toString()) {
        const matchingSite =
          activeReportSites.find(site => site.id.toString() === query.site) ||
          {}
        updateSelectedSite(matchingSite.id)
      }
    }
  }, [
    activeReportSites,
    isFetchingUserSites,
    query.class,
    query.site,
    selectedSite,
  ])

  const navigationHandler = (paramsByKey, type) => {
    const {
      activity,
      classInstance,
      gradeName,
      grades,
      siteId,
      unitSection,
      unit,
    } = paramsByKey
    const gradesSlugs = grades?.map(
      grade => GRADE_NAME_TO_GRADE_SLUG_MAP[grade],
    )
    const activitySlugs = activity?.map(
      activity => ACTIVITY_NAME_TO_SLUG_MAP[activity],
    )
    const unitSlug = UNIT_RANGE_NAME_TO_SLUG_MAP?.[unit] || ''
    const unitSegment = isDwbReportRoute
      ? K8_REPORT_UNIT_SECTION.SPECIALIZED_UNITS
      : unitSection || k8ReportUnitSection || DEFAULT_K8_REPORT_UNIT_SECTION
    const queryParamsByKey = {
      ...(siteId && { site: siteId }),
      ...(classInstance && { class: classInstance }),
      ...(grades?.length > 0 && { grades: gradesSlugs.join(',') }),
      ...(activity?.length > 0 && { activity: activitySlugs.join(',') }),
      ...(unit && { unit: unitSlug }),
    }
    const gradeSlug = K8_GRADE_NAME_TO_GRADE_SLUG_MAP[gradeName] || ''
    const route = buildReportPath({
      variant: reportVariant,
      unitSection: unitSegment,
      gradeSlug,
    })
    const url = NavigationHelper.generateRouteWithQueryParams(
      route,
      queryParamsByKey,
    )

    return navigationEvent(url, type)
  }

  const isSettled =
    !isFetchingUserSites && activeReportSites?.length > 0 && !!entry
  if (!isSettled) {
    return null
  }
  if (!selectedSite) {
    return (
      <ErrorMessage
        additionalDetails={`Don't have access to view site with id: ${query.site}. If you believe you should have access to this site, please contact us.`}
        serviceName={'accessError'}
        statusCode={401}
      />
    )
  }

  if (pathnameNeedsRedirect) {
    return null
  }

  return (
    <LessonCompletionReport
      entry={entry}
      gradeEmptyDisplay={gradeEmptyDisplay}
      isDwbReportRoute={isDwbReportRoute}
      match={match}
      navigationEvent={navigationEvent}
      navigationHandler={navigationHandler}
      schoolEmptyDisplay={schoolEmptyDisplay}
    />
  )
}

const mapStateToProps = (state, ownProps) => {
  const { licenseManager, loadingManager, reportsManager, router } = state
  const { pathname, query } = router.location
  const reportVariant = getReportVariantFromPathname(pathname)
  const isDwbReportRoute = reportVariant === REPORT_VARIANT.DWB
  const activeReportSites = licenseSelectors.selectActiveAdminDwbReportSites(
    state,
  )
  const { isFetchingUserSites } = licenseManager || {}
  const { error, k8Reports, selectedSite } = reportsManager || {}
  const { status: errorStatusCode } = error || {}
  const { isLoading: isLoadingApp } = loadingManager
  const shouldLoad =
    (!activeReportSites || !ownProps.entry || !selectedSite) && !errorStatusCode
  const { location } = router
  const { class: classInstance } = query || {}
  const parsedPath = parseLessonCompletionReportPathname(pathname)
  const { gradeSlugSegment } = parsedPath
  const gradeKey = gradeSlugSegment?.split('-')[1]
  const gradeGraphEnum = K8_GRADE_KEY_TO_GRAPH_ENUM[gradeKey]

  const redirectTargetPath = isDwbReportRoute
    ? null
    : getK8ReportFlagOnRedirectTarget(pathname)
  const pathnameNeedsRedirect = !!redirectTargetPath

  const k8ReportUnitSection = isDwbReportRoute
    ? K8_REPORT_UNIT_SECTION.SPECIALIZED_UNITS
    : parsedPath.unitSection

  const parts = pathname?.split('/').filter(Boolean) || []
  const unitSegment = isDwbReportRoute
    ? K8_REPORT_UNIT_SECTION.SPECIALIZED_UNITS
    : parsedPath.unitSection

  let isSchoolPage
  if (isDwbReportRoute) {
    isSchoolPage =
      !gradeSlugSegment && !query?.class && !!query?.site && !errorStatusCode
  } else {
    isSchoolPage =
      !!unitSegment &&
      parts.length === 3 &&
      !query?.class &&
      !!query?.site &&
      !errorStatusCode
  }
  const isGradePage = isDwbReportRoute
    ? !!gradeSlugSegment && !query.class
    : !!unitSegment && !!gradeSlugSegment?.includes('grade') && !query.class
  const isClassPage = query.class
  const schoolSummaryReport = reportsSelectors.selectSchoolSummaryReport(state)
  const gradeReport = reportsSelectors.selectGradeReport(state)
  const classDetailsReport = reportsSelectors.selectClassDetailsReport(
    state,
    classInstance,
  )
  const selectedSiteDetailsMissing = !!k8Reports?.[selectedSite]
    ?.siteDetailsMissing
  const schoolShouldFetch =
    !pathnameNeedsRedirect &&
    isSchoolPage &&
    !!selectedSite &&
    !schoolSummaryReport
  const gradeShouldFetch =
    !pathnameNeedsRedirect &&
    isGradePage &&
    !isClassPage &&
    selectedSite &&
    !gradeReport
  const classDetailsShouldFetch =
    !pathnameNeedsRedirect &&
    isClassPage &&
    selectedSite &&
    classInstance &&
    (!classDetailsReport || !activeReportSites || !ownProps.entry) &&
    !selectedSiteDetailsMissing &&
    !errorStatusCode
  const gradeAndClassShouldFetch =
    !pathnameNeedsRedirect &&
    pathname?.includes('grade') &&
    query.class &&
    !gradeReport &&
    selectedSite &&
    (!classDetailsReport || !activeReportSites || !ownProps.entry) &&
    !selectedSiteDetailsMissing &&
    !errorStatusCode

  return {
    activeReportSites,
    classDetailsShouldFetch,
    classInstance,
    errorStatusCode,
    gradeAndClassShouldFetch,
    gradeGraphEnum,
    gradeShouldFetch,
    isDwbReportRoute,
    k8ReportUnitSection,
    k8Reports,
    isFetchingUserSites,
    isLoadingApp,
    location,
    pathnameNeedsRedirect,
    query,
    redirectTargetPath,
    reportVariant,
    schoolShouldFetch,
    selectedSite,
    shouldLoad,
  }
}

const mapDispatchToProps = {
  fetchK8ReportClassDetails:
    reportsOperations.fetchK8ReportClassDetailsBySiteId,
  fetchK8ReportGrade: reportsOperations.fetchK8ReportGradeBySiteId,
  fetchK8ReportGradeAndClassDetails:
    reportsOperations.fetchK8ReportGradeAndClassDetailsBySiteId,
  fetchK8ReportSchool: reportsOperations.fetchK8ReportSchoolBySiteId,
  fetchUserSites: licenseOperations.fetchUserSites,
  navigationEvent,
  setAppIsLoadingState: loadingOperations.setIsLoadingState,
  setAppNotLoadingState: loadingOperations.setNotLoadingState,
  updateSelectedSite: reportsOperations.updateSelectedSite,
  updateSelectedProgram: reportsOperations.updateSelectedProgram,
}

const mapper = entry => {
  if (!entry) {
    return {}
  }
  const {
    gradeEmptyStateIcon,
    gradeEmptyStateText,
    gradeEmptyStateHeader,
    schoolEmptyStateIcon,
    schoolEmptyStateText,
    schoolEmptyStateHeader,
  } = entry
  return {
    entry,
    gradeEmptyDisplay: {
      header: gradeEmptyStateHeader,
      icon: gradeEmptyStateIcon,
      text: gradeEmptyStateText,
    },
    schoolEmptyDisplay: {
      header: schoolEmptyStateHeader,
      icon: schoolEmptyStateIcon,
      text: schoolEmptyStateText,
    },
  }
}

LessonCompletionReportContainer.propTypes = {
  activeReportSites: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  classDetailsShouldFetch: PropTypes.bool,
  classInstance: PropTypes.string,
  entry: PropTypes.object,
  errorStatusCode: PropTypes.number,
  fetchK8ReportClassDetails: PropTypes.func,
  fetchK8ReportGrade: PropTypes.func,
  fetchK8ReportGradeAndClassDetails: PropTypes.func,
  fetchK8ReportSchool: PropTypes.func,
  fetchUserSites: PropTypes.func,
  getIdFromQueryParams: PropTypes.func,
  gradeAndClassShouldFetch: PropTypes.bool,
  gradeEmptyDisplay: PropTypes.object,
  gradeGraphEnum: PropTypes.string,
  gradeShouldFetch: PropTypes.bool,
  isDwbReportRoute: PropTypes.bool,
  isFetchingUserSites: PropTypes.bool,
  isLoadingApp: PropTypes.bool,
  k8ReportUnitSection: PropTypes.string,
  location: PropTypes.object,
  match: PropTypes.object,
  navigationEvent: PropTypes.func,
  pathnameNeedsRedirect: PropTypes.bool,
  query: PropTypes.object,
  redirectTargetPath: PropTypes.string,
  reportVariant: PropTypes.string,
  schoolEmptyDisplay: PropTypes.object,
  schoolShouldFetch: PropTypes.bool,
  selectedSite: PropTypes.number,
  setAppIsLoadingState: PropTypes.func,
  setAppNotLoadingState: PropTypes.func,
  shouldLoad: PropTypes.bool,
  updateSelectedProgram: PropTypes.func,
  updateSelectedSite: PropTypes.func,
}

export default ContentEntryDataGetter(
  LessonCompletionReportContainer,
  {
    entryId: ENTRY_ID,
    include: 3,
    mapper,
    spread: true,
  },
  connect(mapStateToProps, mapDispatchToProps),
)
